/* Every transfer of shares, with a way to correct one and a way to delete one.
 *
 * A dated record nobody can find cannot be corrected, and this is a record the
 * cost and tax arithmetic reads — `fifoRealised` uses it to know which account
 * a parcel sits in. So it is listed beside the currency conversions, which is
 * where this book already keeps movements that are not trades.
 *
 * ── why correcting moves shares, when correcting a conversion does not ────
 *
 * A conversion is inert once written: removing the row leaves the balances
 * where they are, because a balance is typed over in one tap anyway. A transfer
 * is not inert. Change the record without moving the shares back and every
 * derivation describes one book while every screen shows another.
 *
 * So a correction reverses what it did and applies what it now says, in one
 * write. The house rule holds — the record still describes exactly one event
 * and is never re-run as a second one.
 *
 * ── and why it sometimes refuses ─────────────────────────────────────────
 *
 * When the destination no longer holds what the transfer put there, because the
 * shares were sold or moved on. Putting them back is arithmetic the book cannot
 * honestly do, so it says which lot is short and sends you to the lots. Netting
 * the difference in silently would be the one thing this app never does. */

import { useState } from 'react';
import { useStore } from '../state/store';
import { accountName } from '../lib/accounts';
import { moneyExact, shares as fmtShares, shortDate } from '../lib/format';
import { decimalOr } from '../lib/number';
import type { ShareTransfer } from '../types';

export function TransferLog({ say }: { say: (m: string) => void }) {
  const { state } = useStore();
  const rows = state.data.transfers ?? [];
  const [editing, setEditing] = useState<string | null>(null);
  if (rows.length === 0) return null;

  return (
    <div className="card card-pad" style={{ marginTop: 10 }}>
      <div className="fin-title">Shares moved between accounts</div>
      <div className="dim" style={{ fontSize: 11, lineHeight: 1.45, marginTop: 6 }}>
        Not trades. Nothing was sold and no gain was realised — the cost travelled with the shares.
        Correcting one moves the shares to match, because the tax figures read these.
      </div>

      {rows.map((x) => (
        <div key={x.id} style={{ marginTop: 12 }}>
          <div className="kv">
            <span className="k">
              {shortDate(x.at)} · <span className="num">{x.ticker}</span>
            </span>
            <span className="v" style={{ fontSize: 12.5 }}>
              <span className="num">{fmtShares(x.shares)}</span>{' '}
              {accountName(state.data.accounts, x.fromAccount)} →{' '}
              {accountName(state.data.accounts, x.toAccount)}
            </span>
          </div>
          <div className="dim" style={{ fontSize: 11, marginTop: 2 }}>
            {x.costPerShare !== undefined
              ? <>cost carried {moneyExact(x.costPerShare, x.currency as 'USD')} a share</>
              : <>no cost on record travelled with them</>}
            {x.fee ? <>{' · '}fee {moneyExact(x.fee, x.currency as 'USD')} off{' '}
              {accountName(state.data.accounts, x.feeAccount ?? x.fromAccount)}</> : null}
          </div>

          {editing === x.id
            ? <EditTransfer record={x} say={say} onClose={() => setEditing(null)} />
            : (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 6 }}
                      onClick={() => setEditing(x.id)}>
                Correct or remove
              </button>
            )}
        </div>
      ))}
    </div>
  );
}

function EditTransfer({ record, say, onClose }: {
  record: ShareTransfer; say: (m: string) => void; onClose: () => void;
}) {
  const { state, correctTransfer, removeTransfer } = useStore();
  const [count, setCount] = useState(String(record.shares));
  const [fee, setFee] = useState(record.fee ? String(record.fee) : '');
  const [at, setAt] = useState(record.at.slice(0, 10));
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [busy, setBusy] = useState(false);
  const [why, setWhy] = useState<string | null>(null);

  const save = async () => {
    if (busy) return;
    setBusy(true);
    setWhy(null);
    try {
      const out = await correctTransfer(record, {
        ticker: record.ticker,
        fromAccount: record.fromAccount,
        toAccount: record.toAccount,
        shares: decimalOr(count),
        at: `${at}T12:00:00.000Z`,
        ...(decimalOr(fee) > 0
          ? { fee: decimalOr(fee), feeAccount: record.feeAccount ?? record.fromAccount }
          : {}),
      });
      if (!out.ok) { setWhy(out.why); return; }
      say(`${record.ticker} transfer restated.`);
      onClose();
    } finally { setBusy(false); }
  };

  const drop = async () => {
    if (busy) return;
    setBusy(true);
    setWhy(null);
    try {
      const out = await removeTransfer(record);
      if (!out.ok) { setWhy(out.why); return; }
      say(`${record.ticker} transfer removed. The shares went back.`);
      onClose();
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--edge)' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor={`tx-n-${record.id}`}>Shares</label>
          <input id={`tx-n-${record.id}`} className="input" data-mono="true" inputMode="decimal"
                 value={count} onChange={(e) => setCount(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label htmlFor={`tx-f-${record.id}`}>Fee</label>
          <input id={`tx-f-${record.id}`} className="input" data-mono="true" inputMode="decimal"
                 value={fee} placeholder="0.00" onChange={(e) => setFee(e.target.value)} />
        </div>
      </div>
      <div className="field">
        <label htmlFor={`tx-d-${record.id}`}>When</label>
        <input id={`tx-d-${record.id}`} className="input" type="date"
               value={at} onChange={(e) => setAt(e.target.value)} />
      </div>

      <div className="note" style={{ marginBottom: 10 }}>
        Changing the count moves the difference between{' '}
        {accountName(state.data.accounts, record.fromAccount)} and{' '}
        {accountName(state.data.accounts, record.toAccount)}. It records no trade and no gain, and
        the fee is reversed and re-applied rather than charged twice.
      </div>

      {why ? <div className="note" data-tone="crit" style={{ marginBottom: 10 }}>{why}</div> : null}

      <div className="fab-row" style={{ marginTop: 0 }}>
        <button className="btn btn-block" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={busy || !(decimalOr(count) > 0)}
                onClick={() => void save()}>
          Save the correction
        </button>
      </div>

      <div className="divider" />
      {confirmRemove ? (
        <>
          <div className="note" data-tone="crit">
            Removing it puts {fmtShares(record.shares)} back on{' '}
            {accountName(state.data.accounts, record.fromAccount)} at the cost they carried, and
            returns the fee. That is right when the move never happened, and wrong when it did —
            the record is what the tax figures read.
          </div>
          <div className="fab-row" style={{ marginTop: 9 }}>
            <button className="btn btn-block" onClick={() => setConfirmRemove(false)}>Keep it</button>
            <button className="btn btn-danger btn-block" disabled={busy} onClick={() => void drop()}>
              Remove the transfer
            </button>
          </div>
        </>
      ) : (
        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmRemove(true)}>
          Remove this transfer
        </button>
      )}
    </div>
  );
}
